import { useId } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * One section fold as `PlayerIdentity.dc.html` draws it (#1290, #1388 F1).
 *
 * Its own component, not a mode of `SectionCard`, so no other `SectionCard`
 * host moves (D217, #1359). The header is the board's: a 36px icon tile, then
 * a column holding the title and the pill in a WRAPPING row with the
 * description under it, then the chevron as a separate flex item outside that
 * row. The pill therefore drops under the title when it does not fit and the
 * title keeps the full width of its column; the shared r5 header keeps the
 * pill and chevron on the title's row, which left a 86px title box at 360.
 *
 * The header marks itself `data-nw-wrap-title`: its title is judged by the
 * wrap rule in `e2e/foldTitleRule.js` (no mid-word break, no extra lines),
 * not by the SectionCard 110px floor (D351).
 *
 * Open state is the caller's, so the page can keep one section open.
 * Reading contract kept from `SectionCard`: the trigger carries
 * `aria-expanded` + `aria-controls` and `toggleTestid`, the pill
 * `${toggleTestid}-summary`, and the panel stays in the DOM while folded with
 * its content unmounted.
 */
export default function IdentityFold({ icon: Icon, title, desc, summary, open, onToggle, toggleTestid, children }) {
  const panelId = useId();
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        data-testid={toggleTestid}
        data-nw-wrap-title=""
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          width: "100%",
          minHeight: 56,
          boxSizing: "border-box",
          padding: "12px 14px",
          textAlign: "left",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          minWidth: 0,
        }}
      >
        <span
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: 10,
            background: "var(--nw-navy-tint)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={16} color="var(--nw-navy)" aria-hidden="true" />
        </span>
        <span style={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1, minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <h3
              style={{
                margin: 0,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: 16,
                color: "var(--nw-ink)",
                overflowWrap: "break-word",
              }}
            >
              {title}
            </h3>
            <span
              data-testid={toggleTestid ? `${toggleTestid}-summary` : undefined}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px",
                background: "var(--nw-navy-tint)",
                color: "var(--nw-navy)",
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 999,
                whiteSpace: "nowrap",
              }}
            >
              {summary}
            </span>
          </span>
          <span style={{ fontSize: 13, lineHeight: 1.45, color: "var(--nw-ink-muted)" }}>{desc}</span>
        </span>
        <Chevron size={16} color="var(--nw-navy)" strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0 }} />
      </button>
      <div
        id={panelId}
        hidden={!open}
        style={open ? { display: "flex", flexDirection: "column", gap: 12, padding: "0 14px 14px" } : undefined}
      >
        {open && (
          <>
            <div style={{ height: 1, background: "var(--nw-line)" }} />
            {children}
          </>
        )}
      </div>
    </div>
  );
}
